import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db, storage } from "../../firebase/firebase.js";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  Box,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { isValid } from "date-fns";
import {
  inchesToCm,
  cmToInches,
  lbsToKg,
  kgToLbs,
} from "../../utils/unitConversion"; // Adjusted path

// import FactEditor from "../profile/FactEditor.jsx"; // Removed as per subtask
import ProfileKGGraph from "../profile/ProfileKGGraph.jsx";

// Constants from ProfileFactsPage.js
const DEFAULT_AVATAR_URL =
  "https://firebasestorage.googleapis.com/v0/b/assistant-49bb1.appspot.com/o/avatars%2Fdefault_avatar.png?alt=media&token=1defaulttoken";
const TARGET_AGENT_DISPLAY_NAME = "Profile Coordinator";
const TARGET_AGENT_ID = "coordinator_profile";

function Profile() {
  const uid = auth.currentUser?.uid;
  const defaultGoals = ["Exercise", "Nutrition"];
  const [userData, setUserData] = useState({
    firstname: "",
    lastname: "",
    gender: "",
    birthDate: "",
    height: "",
    weight: "",
    educationLevel: "",
    socioeconomicStatus: "",
    maritalStatus: "",
    householdSize: "",
    dietaryRestrictions: "",
    gymAccess: "",
    disabilities: "",
    injuries: "",
    currentHealthIssues: "",
    medications: "",
    allergies: "",
    currentCountry: "",
    currentState: "",
    currentCity: "",
    homeCountry: "",
    homeState: "",
    homeCity: "",
    goals: defaultGoals,
    photoURL: "",
    unitPreference: "metric",
    academicMajor: "",
    career: "",
  });
  const [newGoal, setNewGoal] = useState("");
  const [newProfilePic, setNewProfilePic] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [imageFileFromUrl, setImageFileFromUrl] = useState(null);
  const [snack, setSnack] = useState({ message: "", severity: "info" });
  const [loading, setLoading] = useState(false);
  const [hasPendingUpdates, setHasPendingUpdates] = useState(false);
  const navigate = useNavigate();

  // State variables from ProfileFactsPage.js
  const [textInput, setTextInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const accentTone = useMemo(
    () =>
      theme.palette.secondary?.main ??
      theme.palette.primary.light ??
      theme.palette.primary.main,
    [theme]
  );

  const pageBackground = useMemo(() => {
    const base =
      theme.palette.mode === "light"
        ? theme.palette.grey[100]
        : theme.palette.background.default;
    const primaryGlow = alpha(
      theme.palette.primary.main,
      theme.palette.mode === "light" ? 0.12 : 0.35
    );
    const accentGlow = alpha(
      accentTone,
      theme.palette.mode === "light" ? 0.1 : 0.3
    );
    return `radial-gradient(circle at 0% -20%, ${primaryGlow} 0%, transparent 55%), radial-gradient(circle at 120% -10%, ${accentGlow} 0%, transparent 50%), ${base}`;
  }, [accentTone, theme]);

  const cardSurface = useMemo(
    () => ({
      position: "relative",
      overflow: "hidden",
      borderRadius: 3,
      background:
        theme.palette.mode === "light"
          ? `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.9
            )} 0%, ${alpha(accentTone, 0.08)} 120%)`
          : `linear-gradient(135deg, ${alpha(
              theme.palette.primary.dark ?? theme.palette.primary.main,
              0.55
            )} 0%, ${alpha(accentTone, 0.32)} 120%)`,
      border: `1px solid ${alpha(
        theme.palette.primary.main,
        theme.palette.mode === "light" ? 0.18 : 0.42
      )}`,
      boxShadow: theme.shadows[6],
      backdropFilter: "blur(18px)",
    }),
    [accentTone, theme]
  );

  const inputBaseStyles = useMemo(
    () => ({
      borderRadius: 2,
      backgroundColor:
        theme.palette.mode === "light"
          ? alpha(theme.palette.common.white, 0.92)
          : alpha(theme.palette.background.default, 0.6),
      '& fieldset': {
        borderColor: alpha(theme.palette.text.primary, 0.12),
      },
      '&:hover fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.45),
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1.5,
      },
    }),
    [theme]
  );

  const cardContentBase = useMemo(
    () => ({
      px: { xs: 2.5, md: 3 },
      py: { xs: 2.5, md: 3 },
      display: "flex",
      flexDirection: "column",
      gap: 2.5,
      '& .MuiOutlinedInput-root': inputBaseStyles,
      '& .MuiInputLabel-root': {
        color: alpha(theme.palette.text.primary, 0.65),
      },
      '& .MuiInputLabel-root.Mui-focused': {
        color: theme.palette.primary.main,
      },
    }),
    [inputBaseStyles, theme]
  );

  const sectionHeadingSx = useMemo(
    () => ({
      fontWeight: 700,
      letterSpacing: 0.3,
      color:
        theme.palette.mode === "light"
          ? alpha(theme.palette.text.primary, 0.85)
          : alpha(theme.palette.common.white, 0.9),
    }),
    [theme]
  );

  const heroGradient = useMemo(
    () =>
      theme.palette.mode === "light"
        ? `radial-gradient(circle at 15% 20%, ${alpha(
            theme.palette.common.white,
            0.5
          )} 0%, transparent 55%)`
        : `radial-gradient(circle at 15% 20%, ${alpha(
            theme.palette.primary.light ?? theme.palette.primary.main,
            0.25
          )} 0%, transparent 60%)`,
    [theme]
  );

  const primaryGradient = useMemo(
    () =>
      theme.palette.mode === "light"
        ? `linear-gradient(120deg, ${theme.palette.primary.main}, ${accentTone})`
        : `linear-gradient(120deg, ${alpha(
            theme.palette.primary.light ?? theme.palette.primary.main,
            0.95
          )}, ${alpha(accentTone, 0.88)})`,
    [accentTone, theme]
  );

  const primaryGradientHover = useMemo(
    () =>
      theme.palette.mode === "light"
        ? `linear-gradient(120deg, ${theme.palette.primary.dark}, ${accentTone})`
        : `linear-gradient(120deg, ${alpha(
            theme.palette.primary.light ?? theme.palette.primary.main,
            1
          )}, ${alpha(accentTone, 0.95)})`,
    [accentTone, theme]
  );

  // Helper to check if a value is a positive number
  const isPositiveNumber = (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && num > 0;
  };

  useEffect(() => {
    // Fetch user data from Firestore
    const fetchData = async () => {
      if (!uid) return;
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const goals = Array.isArray(data.goals) ? data.goals : [];
        const { region, unitPreference, ...rest } = data; // region is obsolete
        const missing = defaultGoals.filter((g) => !goals.includes(g));
        setUserData({
          ...userData,
          ...rest,
          goals: [...missing, ...goals],
          unitPreference: unitPreference || "metric",
        });
      }
    };
    fetchData();
  }, [uid]); // Added uid to dependency array as it's used in the effect

  // useEffect to listen for pending updates
  useEffect(() => {
    if (!uid) {
      setHasPendingUpdates(false);
      return;
    }

    const pendingUpdatesColRef = collection(
      db,
      "users",
      uid,
      "pendingProfileUpdates"
    );
    const unsubscribe = onSnapshot(
      pendingUpdatesColRef,
      (snapshot) => {
        setHasPendingUpdates(!snapshot.empty);
      },
      (error) => {
        console.error("Error fetching pending updates:", error);
        setHasPendingUpdates(false); // Optionally set to false on error
      }
    );

    // Cleanup listener on component unmount or uid change
    return () => unsubscribe();
  }, [uid]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  const handleUnitSensitiveInputChange = (e) => {
    const { name, value } = e.target;
    let metricValue = value;

    if (value === "") {
      // Allow clearing the field
      setUserData({ ...userData, [name]: "" });
      return;
    }

    // Basic validation for numeric input (allowing decimal)
    if (!/^\d*\.?\d*$/.test(value)) {
      setSnack({
        message: `❌ Please enter a valid number for ${name}.`,
        severity: "error",
      });
      // Keep the invalid input visible for correction by user
      setUserData({ ...userData, [name]: value });
      return;
    } else if (snack.message.includes(name)) {
      // Clear snack if it was about this field
      setSnack({ message: "", severity: "info" });
    }

    if (userData.unitPreference === "imperial") {
      if (name === "height") {
        metricValue = inchesToCm(value);
      } else if (name === "weight") {
        metricValue = lbsToKg(value);
      }
    }
    // Ensure metricValue is stored as a string if it's a number, or keep as empty string
    setUserData({
      ...userData,
      [name]:
        typeof metricValue === "number" ? String(metricValue) : metricValue,
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewProfilePic(file);
      setImageUrlInput(""); // Clear URL input if a file is chosen
      setImageFileFromUrl(null); // Clear any image file from URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFetchImageFromUrl = async () => {
    if (!imageUrlInput.trim()) return;
    setLoading(true);
    setSnack({ message: "", severity: "info" }); // Clear previous snacks

    try {
      const response = await fetch(imageUrlInput.trim());
      if (response.ok) {
        const blob = await response.blob();
        // Determine a filename, try to get it from URL or default
        let fileName = "profile_image_from_url";
        try {
          const urlPath = new URL(imageUrlInput.trim()).pathname;
          const lastSegment = urlPath.substring(urlPath.lastIndexOf('/') + 1);
          if (lastSegment) {
            fileName = lastSegment.split('.')[0]; // Get name before extension
          }
        } catch (e) {
          console.warn("Could not parse filename from URL, using default.", e);
        }
        // Add appropriate extension based on blob type or default to .png
        const extension = blob.type.split('/')[1] || 'png';
        const imageFile = new File([blob], `${fileName}.${extension}`, { type: blob.type });

        setNewProfilePic(imageFile);
        setImageFileFromUrl(null); // Not strictly necessary if newProfilePic is the source of truth

        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(blob); // Use blob for FileReader

        // Clear the actual file input element
        const fileInput = document.getElementById("profilePicFileInput");
        if (fileInput) {
          fileInput.value = null;
        }

        setSnack({ message: "Image loaded from URL successfully!", severity: "success" });
      } else {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error fetching image from URL:", error);
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        setSnack({
          message: "The image URL is restricted by CORS. Please try another image, or download it first and then upload the file.",
          severity: "error",
          autoHideDuration: 8000 // Longer duration for important messages
        });
      } else {
        setSnack({ message: `Error fetching image: ${error.message}`, severity: "error" });
      }
      setPreviewUrl(userData.photoURL || null); // Revert to original or null if no original
      setNewProfilePic(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = () => {
    const trimmed = newGoal.trim();
    if (trimmed && !userData.goals.includes(trimmed)) {
      setUserData({ ...userData, goals: [...userData.goals, trimmed] });
    }
    setNewGoal("");
  };

  const handleGoalKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddGoal();
    }
  };

  const handleDeleteGoal = (goal) => () => {
    if (defaultGoals.includes(goal)) return;
    setUserData({
      ...userData,
      goals: userData.goals.filter((g) => g !== goal),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!userData.birthDate || !isValid(new Date(userData.birthDate))) {
      setSnack({ message: "❌ Invalid birth date", severity: "error" });
      setLoading(false);
      return;
    }

    // Validate height - userData.height should always be metric (cm)
    if (userData.height && !isPositiveNumber(userData.height)) {
      setSnack({
        message: `❌ Height must be a valid positive number.`,
        severity: "error",
      });
      setLoading(false);
      return;
    }

    // Validate weight - userData.weight should always be metric (kg)
    if (userData.weight && !isPositiveNumber(userData.weight)) {
      setSnack({
        message: `❌ Weight must be a valid positive number.`,
        severity: "error",
      });
      setLoading(false);
      return;
    }

    let uploadedPhotoURL = userData.photoURL; // Keep existing photoURL by default
    let messageSentSuccessfully = false;

    try {
      // Step 1: Handle new profile picture upload (if any)
      if (newProfilePic) {
        const storageRefPath = `profilePictures/${uid}`;
        const storageRefInstance = ref(storage, storageRefPath);
        const snap = await uploadBytes(storageRefInstance, newProfilePic);
        uploadedPhotoURL = await getDownloadURL(snap.ref);
        // Update userData with the new photoURL for message construction, but not for direct user doc update
        // setUserData({ ...userData, photoURL: uploadedPhotoURL }); // This might cause a race condition or re-render issue, avoid for now
      }

      // Step 2: Construct natural language message<<<<<<< feature/profile-consolidation-kg-integration
      const excludedKeys = [
        "email",
        "domains",
        "durationToAlert",
        "primaryGoal",
        "selectedProject",
        "photoURL",
        "goals",
        "region",
      ];
      let messageContent =
        "My profile information has been updated. Here are the details:\n";
      for (const key in userData) {
        if (Object.hasOwnProperty.call(userData, key)) {
          const value = userData[key];
          // Exclude specified keys and fields that might typically be empty.
          if (value && !excludedKeys.includes(key)) {
            // Convert camelCase to Title Case for readability
            const readableKey = key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase());
            if (key === "height") {
              messageContent += `${readableKey}: ${value} cm.\n`;
            } else if (key === "weight") {
              messageContent += `${readableKey}: ${value} kg.\n`;
            } else if (key !== "timezone") { // Explicitly skip timezone here
              messageContent += `${readableKey}: ${value}.\n`;
            }
          }
        }
      }

      if (userData.goals && userData.goals.length > 0) {
        messageContent += `My goals are: ${userData.goals.join(", ")}.\n`;
      }

      if (newProfilePic && uploadedPhotoURL) {
        messageContent += `My new profile photo URL is: ${uploadedPhotoURL}.\n`;
      } else if (!newProfilePic && userData.photoURL) {
        // If no new pic, but an existing one, mention it for completeness if desired, or omit.
        // For now, we only explicitly mention *new* photo URLs.
      }

      // Step 3: Send the message to coordinator_profile agent
      const currentUid = auth.currentUser?.uid;
      if (!currentUid) {
        throw new Error("User not authenticated.");
      }

      let targetAgentPhotoURL = DEFAULT_AVATAR_URL;
      try {
        const agentDocRef = doc(
          db,
          "users",
          currentUid,
          "agents",
          TARGET_AGENT_ID
        );
        const agentSnap = await getDoc(agentDocRef);
        if (agentSnap.exists()) {
          const agentData = agentSnap.data();
          targetAgentPhotoURL = agentData.photoURL || DEFAULT_AVATAR_URL;
        }
      } catch (error) {
        console.error("Error fetching target agent data for message:", error);
        // Non-critical, proceed with default avatar
      }

      const messageData = {
        uid: currentUid,
        content: messageContent,
        role: "user",
        agent: TARGET_AGENT_ID,
        agentDisplayName: TARGET_AGENT_DISPLAY_NAME,
        photoURL: targetAgentPhotoURL, // This is the photoURL of the *target agent* for display in chat, not the user's new profile pic
        timestamp: serverTimestamp(),
      };

      await addDoc(collection(db, "messages"), messageData);
      messageSentSuccessfully = true;

      // Reinstate Firestore Document Update:
      const dataToSave = { ...userData };

      if (newProfilePic && uploadedPhotoURL) {
        dataToSave.photoURL = uploadedPhotoURL;
      }

      // Map gymAccess
      let mappedGymAccess = ""; // Default to empty or 'no' if that's preferred
      if (typeof dataToSave.gymAccess === "string") {
        const lowerGymAccess = dataToSave.gymAccess.toLowerCase();
        if (lowerGymAccess === "true" || lowerGymAccess === "yes") {
          mappedGymAccess = "yes";
        } else if (lowerGymAccess === "false" || lowerGymAccess === "no") {
          mappedGymAccess = "no";
        }
      } else if (typeof dataToSave.gymAccess === "boolean") {
        mappedGymAccess = dataToSave.gymAccess ? "yes" : "no";
      }
      dataToSave.gymAccess = mappedGymAccess;

      // Remove fields not intended for direct save if necessary e.g.
      // delete dataToSave.region; // region is already handled by destructuring in useEffect

      if (!uid) {
        throw new Error("User UID is not available for Firestore update.");
      }
      await updateDoc(doc(db, "users", uid), dataToSave);
      console.log(
        "User document directly updated in Profile.js handleSubmit with data:",
        dataToSave
      );

      setSnack({
        message: "Profile updated successfully ✔",
        severity: "success",
      });
      if (newProfilePic) {
        setNewProfilePic(null); // Clear the new profile pic input
        setPreviewUrl(uploadedPhotoURL); // Update preview to show the uploaded picture
        // Also update the userData state to reflect the new photoURL immediately in the UI if it was part of userData
        setUserData((prevData) => ({
          ...prevData,
          photoURL: uploadedPhotoURL,
        }));
      }
      setImageUrlInput(""); // Clear the URL input field after successful submission
    } catch (err) {
      console.error("Error in handleSubmit:", err);
      setSnack({
        message: `❌ Update failed: ${err.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function from ProfileFactsPage.js (handleProcessText - for the separate message box)
  const handleProcessText = async () => {
    setIsProcessing(true);
    const currentUid = auth.currentUser?.uid;
    if (!currentUid) {
      console.error("No user logged in.");
      setSnack({
        message: "❌ Please log in to process text.",
        severity: "error",
      });
      setIsProcessing(false);
      return;
    }

    if (!textInput.trim()) {
      // console.log("Text input is empty. Nothing to process.");
      setSnack({
        message: "ℹ️ Please enter some text to process.",
        severity: "info",
      });
      setIsProcessing(false);
      return;
    }

    let targetAgentPhotoURL = DEFAULT_AVATAR_URL;

    if (currentUid) {
      try {
        const agentDocRef = doc(
          db,
          "users",
          currentUid,
          "agents",
          TARGET_AGENT_ID
        );
        const agentSnap = await getDoc(agentDocRef);
        if (agentSnap.exists()) {
          const agentData = agentSnap.data();
          targetAgentPhotoURL = agentData.photoURL || DEFAULT_AVATAR_URL;
        }
      } catch (error) {
        console.error("Error fetching target agent data:", error);
        // Fallbacks are already set
      }
    }

    const messageData = {
      uid: currentUid,
      content: textInput,
      role: "user",
      agent: TARGET_AGENT_ID,
      agentDisplayName: TARGET_AGENT_DISPLAY_NAME,
      photoURL: targetAgentPhotoURL,
      timestamp: serverTimestamp(),
    };

    try {
      await addDoc(collection(db, "messages"), messageData);
      // console.log("Message written with ID: ", docRef.id);
      setSnack({
        message: "Message sent to Profile Coordinator ✔",
        severity: "success",
      });
      setTextInput("");
    } catch (error) {
      console.error("Error adding message: ", error);
      setSnack({
        message: `❌ Error sending message: ${error.message}`,
        severity: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: "100%",
        py: { xs: 2.5, md: 4 },
        px: { xs: 2, md: 4 },
        background: pageBackground,
        transition: "background 320ms ease",
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            ...cardSurface,
            px: { xs: 2.75, md: 3.75 },
            py: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2.5, md: 3.5 },
            alignItems: { xs: "flex-start", md: "center" },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: heroGradient,
              opacity: 0.9,
            }}
          />
          <Box
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Typography variant="overline" sx={{ letterSpacing: 1.6, opacity: 0.72 }}>
              Profile Command Center
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Edit Profile
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: 640,
                color:
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.text.primary, 0.78)
                    : alpha(theme.palette.common.white, 0.9),
              }}
            >
              Keep your personal information up to date so your coaches can tailor check-ins, nudges, and plans around what matters most to you.
            </Typography>
          </Box>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              minWidth: { md: 240 },
            }}
          >
            {hasPendingUpdates ? (
              <Button
                onClick={() => navigate("/profile/updates")}
                data-testid="profile-pending-updates-button"
                sx={{
                  borderRadius: 999,
                  px: 2.8,
                  py: 1.1,
                  fontWeight: 600,
                  letterSpacing: 0.35,
                  backgroundColor: alpha(accentTone, 0.18),
                  color:
                    theme.palette.mode === "light"
                      ? theme.palette.primary.main
                      : alpha(theme.palette.common.white, 0.9),
                  border: `1px solid ${alpha(accentTone, 0.45)}`,
                  boxShadow: theme.shadows[4],
                  '&:hover': {
                    backgroundColor: alpha(accentTone, 0.28),
                  },
                }}
              >
                Review Pending Updates
              </Button>
            ) : (
              <Typography
                variant="caption"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 1.1,
                  color:
                    theme.palette.mode === "light"
                      ? alpha(theme.palette.text.primary, 0.6)
                      : alpha(theme.palette.common.white, 0.7),
                }}
              >
                Everything looks synced
              </Typography>
            )}
            <Chip
              label={`Goals: ${(userData.goals || defaultGoals).length}`}
              sx={{
                alignSelf: { xs: "flex-start", md: "flex-start" },
                fontWeight: 600,
                letterSpacing: 0.3,
                backgroundColor: alpha(accentTone, 0.18),
                color:
                  theme.palette.mode === "light"
                    ? theme.palette.primary.main
                    : alpha(theme.palette.common.white, 0.9),
              }}
            />
          </Box>
        </Box>

        <Grid container spacing={{ xs: 2.5, md: 3.5 }} alignItems="stretch">
          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSurface, height: "100%" }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  background:
                    theme.palette.mode === "light"
                      ? `radial-gradient(circle at -10% 30%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 65%)`
                      : `radial-gradient(circle at -10% 30%, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.25)} 0%, transparent 65%)`,
                  opacity: 0.85,
                }}
              />
              <CardContent
                sx={{
                  ...cardContentBase,
                  alignItems: "center",
                  textAlign: "center",
                  gap: 2.5,
                }}
              >
                <Avatar
                  src={previewUrl || userData.photoURL}
                  sx={{
                    width: 150,
                    height: 150,
                    boxShadow: theme.shadows[6],
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Profile Photo
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        theme.palette.mode === "light"
                          ? alpha(theme.palette.text.primary, 0.7)
                          : alpha(theme.palette.common.white, 0.75),
                    }}
                  >
                    A friendly face helps your coaches recognize you instantly.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  component="label"
                  disabled={loading}
                  data-testid="profile-pic-upload-button"
                  sx={{
                    borderRadius: 999,
                    px: 2.75,
                    py: 1.05,
                    fontWeight: 600,
                    letterSpacing: 0.35,
                    textTransform: "none",
                    backgroundImage: primaryGradient,
                    boxShadow: theme.shadows[6],
                    '&:hover': {
                      backgroundImage: primaryGradientHover,
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  Upload Picture
                  <input
                    id="profilePicFileInput"
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                </Button>
                {loading && <CircularProgress size={24} sx={{ mt: -1 }} />}
                <TextField
                  label="Or paste image URL"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  fullWidth
                  data-testid="profile-pic-url-input"
                />
                <Button
                  variant="outlined"
                  onClick={handleFetchImageFromUrl}
                  disabled={loading || !imageUrlInput.trim()}
                  data-testid="profile-pic-use-url-button"
                  sx={{
                    borderRadius: 999,
                    px: 2.5,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    textTransform: "none",
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    color:
                      theme.palette.mode === "light"
                        ? alpha(theme.palette.primary.main, 0.85)
                        : alpha(theme.palette.common.white, 0.9),
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  Use URL
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2.5, md: 3 },
              }}
            >
              <Card sx={{ ...cardSurface }}>
                <CardContent sx={cardContentBase}>
                  <Typography variant="h6" sx={sectionHeadingSx}>
                    Personal Details
                  </Typography>
                  <Grid container spacing={2} sx={{ alignItems: "center" }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        name="firstname"
                        value={userData.firstname}
                        data-testid="profile-firstname-input"
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        name="lastname"
                        value={userData.lastname}
                        data-testid="profile-lastname-input"
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth data-testid="profile-gender-select">
                        <InputLabel>Gender</InputLabel>
                        <Select
                          name="gender"
                          value={userData.gender}
                          label="Gender"
                          onChange={handleInputChange}
                          disabled={loading}
                        >
                          <MenuItem value="male">Male</MenuItem>
                          <MenuItem value="female">Female</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Birth Date"
                        name="birthDate"
                        type="date"
                        data-testid="profile-birthdate-input"
                        InputLabelProps={{ shrink: true }}
                        value={userData.birthDate}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ ...cardSurface }}>
                <CardContent sx={cardContentBase}>
                  <Typography variant="h6" sx={sectionHeadingSx}>
                    Physical Attributes
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={`Height (${userData.unitPreference === "metric" ? "cm" : "in"})`}
                        name="height"
                        type="text"
                        data-testid="profile-height-input"
                        value={
                          userData.height === ""
                            ? ""
                            : userData.unitPreference === "metric"
                            ? userData.height
                            : cmToInches(userData.height)
                        }
                        onChange={handleUnitSensitiveInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={`Weight (${userData.unitPreference === "metric" ? "kg" : "lbs"})`}
                        name="weight"
                        type="text"
                        data-testid="profile-weight-input"
                        value={
                          userData.weight === ""
                            ? ""
                            : userData.unitPreference === "metric"
                            ? userData.weight
                            : kgToLbs(userData.weight)
                        }
                        onChange={handleUnitSensitiveInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Unit Preference</InputLabel>
                        <Select
                          name="unitPreference"
                          value={userData.unitPreference}
                          label="Unit Preference"
                          onChange={handleInputChange}
                          disabled={loading}
                        >
                          <MenuItem value="metric">Metric</MenuItem>
                          <MenuItem value="imperial">Imperial</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Gym Access"
                        name="gymAccess"
                        value={userData.gymAccess}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ ...cardSurface }}>
                <CardContent sx={cardContentBase}>
                  <Typography variant="h6" sx={sectionHeadingSx}>
                    Lifestyle & Context
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Education Level"
                        name="educationLevel"
                        value={userData.educationLevel}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Career"
                        name="career"
                        value={userData.career}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Household Size"
                        name="householdSize"
                        value={userData.householdSize}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Marital Status"
                        name="maritalStatus"
                        value={userData.maritalStatus}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Dietary Restrictions"
                        name="dietaryRestrictions"
                        value={userData.dietaryRestrictions}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Allergies"
                        name="allergies"
                        value={userData.allergies}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Medications"
                        name="medications"
                        value={userData.medications}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Current Health Issues"
                        name="currentHealthIssues"
                        value={userData.currentHealthIssues}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Disabilities"
                        name="disabilities"
                        value={userData.disabilities}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Injuries"
                        name="injuries"
                        value={userData.injuries}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ ...cardSurface }}>
                <CardContent sx={cardContentBase}>
                  <Typography variant="h6" sx={sectionHeadingSx}>
                    Where You Are Now
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Country"
                        name="currentCountry"
                        value={userData.currentCountry}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="State"
                        name="currentState"
                        value={userData.currentState}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="City"
                        name="currentCity"
                        value={userData.currentCity}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ ...cardSurface }}>
                <CardContent sx={cardContentBase}>
                  <Typography variant="h6" sx={sectionHeadingSx}>
                    Where You Grew Up
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Country"
                        name="homeCountry"
                        value={userData.homeCountry}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="State"
                        name="homeState"
                        value={userData.homeState}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="City"
                        name="homeCity"
                        value={userData.homeCity}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ ...cardSurface }}>
                <CardContent sx={cardContentBase}>
                  <Typography variant="h6" sx={sectionHeadingSx}>
                    Goals
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                    {(userData.goals || defaultGoals).map((goal, index) => (
                      <Chip
                        key={goal}
                        label={goal}
                        onDelete={
                          defaultGoals.includes(goal)
                            ? undefined
                            : handleDeleteGoal(goal)
                        }
                        data-testid={
                          index === defaultGoals.length && !defaultGoals.includes(goal)
                            ? "profile-goal-chip-example"
                            : undefined
                        }
                      />
                    ))}
                  </Box>
                  <TextField
                    margin="normal"
                    fullWidth
                    label="Add Goal"
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    onKeyDown={handleGoalKeyDown}
                    disabled={loading}
                    data-testid="profile-add-goal-input"
                  />
                </CardContent>
              </Card>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => console.log("Connect Google Calendar clicked")}
                data-testid="profile-google-calendar-button"
                sx={{
                  borderRadius: 2.5,
                  px: 2.5,
                  py: 1.1,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  color:
                    theme.palette.mode === "light"
                      ? alpha(theme.palette.primary.main, 0.85)
                      : alpha(theme.palette.common.white, 0.9),
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                Connect Google Calendar
              </Button>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                data-testid="profile-update-button"
                sx={{
                  borderRadius: 2.5,
                  px: 2.5,
                  py: 1.1,
                  fontWeight: 700,
                  letterSpacing: 0.35,
                  backgroundImage: primaryGradient,
                  boxShadow: theme.shadows[6],
                  '&:hover': {
                    backgroundImage: primaryGradientHover,
                    boxShadow: theme.shadows[9],
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Update Profile"
                )}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ ...cardSurface }}>
              <CardContent sx={cardContentBase}>
                <Typography variant="h5" sx={sectionHeadingSx}>
                  My Knowledge Graph
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color:
                      theme.palette.mode === "light"
                        ? alpha(theme.palette.text.primary, 0.7)
                        : alpha(theme.palette.common.white, 0.75),
                  }}
                >
                  This interactive knowledge graph lets you explore your personal information. Use mouse wheel or pinch to zoom and drag to pan. Click &gt; or &lt; next to a node to expand or collapse its descendants, and click the ✎ icon to edit a fact.
                </Typography>
                {uid && (
                  <ProfileKGGraph
                    uid={uid}
                    data-testid="profile-kg-graph-container"
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ ...cardSurface }}>
              <CardContent sx={cardContentBase}>
                <Typography variant="h5" sx={sectionHeadingSx}>
                  Message to Profile Coordinator
                </Typography>
                <TextField
                  label="Enter your information here"
                  multiline
                  rows={4}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  variant="outlined"
                  data-testid="profile-message-coordinator-input"
                  fullWidth
                  disabled={isProcessing}
                />
                <Button
                  variant="contained"
                  onClick={handleProcessText}
                  disabled={isProcessing || loading}
                  data-testid="profile-message-coordinator-send-button"
                  sx={{
                    alignSelf: "flex-start",
                    borderRadius: 999,
                    px: 2.75,
                    py: 1,
                    fontWeight: 600,
                    letterSpacing: 0.35,
                    backgroundImage: primaryGradient,
                    boxShadow: theme.shadows[6],
                    '&:hover': {
                      backgroundImage: primaryGradientHover,
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  {isProcessing ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Process Text"
                  )}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={!!snack.message}
        autoHideDuration={6000}
        onClose={() => setSnack({ message: "", severity: "info" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack({ message: "", severity: "info" })}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Profile;
